import { useCallback, useRef, useState } from "react";
import type {
  MouseEvent as ReactMouseEvent,
  TouchEvent as ReactTouchEvent,
  WheelEvent as ReactWheelEvent,
} from "react";
import { ActionIcon, Group, Modal, Text } from "@mantine/core";
import { IconZoomIn, IconZoomOut, IconZoomReset } from "@tabler/icons-react";

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const ZOOM_STEP = 0.5;

function clamp(n: number) {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, n));
}

function touchDistance(touches: React.TouchList) {
  const [a, b] = [touches[0], touches[1]];
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

// Full-size pan/zoom viewer opened by clicking a photo or document thumbnail.
// Scroll/pinch to zoom, drag to pan, double-click to toggle, buttons for the rest.
export function ZoomModal({
  opened,
  onClose,
  src,
  alt,
  title,
}: {
  opened: boolean;
  onClose: () => void;
  src?: string | null;
  alt?: string;
  title?: string;
}) {
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragOrigin = useRef<{ x: number; y: number; posX: number; posY: number } | null>(null);
  const pinchOrigin = useRef<{ dist: number; scale: number } | null>(null);

  const reset = useCallback(() => {
    setScale(1);
    setPos({ x: 0, y: 0 });
  }, []);

  function handleClose() {
    reset();
    onClose();
  }

  function zoomBy(delta: number) {
    setScale((s) => {
      const next = clamp(s + delta);
      if (next === MIN_SCALE) setPos({ x: 0, y: 0 });
      return next;
    });
  }

  function handleWheel(e: ReactWheelEvent<HTMLDivElement>) {
    e.preventDefault();
    zoomBy(e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP);
  }

  function handleDoubleClick() {
    setScale((s) => (s > 1 ? 1 : 2.5));
    setPos({ x: 0, y: 0 });
  }

  function handleMouseDown(e: ReactMouseEvent<HTMLDivElement>) {
    if (scale <= 1) return;
    setDragging(true);
    dragOrigin.current = { x: e.clientX, y: e.clientY, posX: pos.x, posY: pos.y };
  }

  function handleMouseMove(e: ReactMouseEvent<HTMLDivElement>) {
    if (!dragOrigin.current) return;
    setPos({
      x: dragOrigin.current.posX + (e.clientX - dragOrigin.current.x),
      y: dragOrigin.current.posY + (e.clientY - dragOrigin.current.y),
    });
  }

  function endDrag() {
    dragOrigin.current = null;
    setDragging(false);
  }

  function handleTouchStart(e: ReactTouchEvent<HTMLDivElement>) {
    if (e.touches.length === 2) {
      pinchOrigin.current = { dist: touchDistance(e.touches), scale };
    } else if (e.touches.length === 1 && scale > 1) {
      const t = e.touches[0];
      setDragging(true);
      dragOrigin.current = { x: t.clientX, y: t.clientY, posX: pos.x, posY: pos.y };
    }
  }

  function handleTouchMove(e: ReactTouchEvent<HTMLDivElement>) {
    if (e.touches.length === 2 && pinchOrigin.current) {
      const ratio = touchDistance(e.touches) / pinchOrigin.current.dist;
      setScale(clamp(pinchOrigin.current.scale * ratio));
    } else if (e.touches.length === 1 && dragOrigin.current) {
      const t = e.touches[0];
      setPos({
        x: dragOrigin.current.posX + (t.clientX - dragOrigin.current.x),
        y: dragOrigin.current.posY + (t.clientY - dragOrigin.current.y),
      });
    }
  }

  function handleTouchEnd(e: ReactTouchEvent<HTMLDivElement>) {
    if (e.touches.length < 2) pinchOrigin.current = null;
    if (e.touches.length === 0) endDrag();
  }

  const isReset = scale === 1 && pos.x === 0 && pos.y === 0;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      size="90%"
      centered
      title={title}
      padding={0}
      styles={{ body: { padding: 0 }, header: { paddingInline: 16 } }}
    >
      <div
        style={{
          position: "relative",
          height: "min(78vh, 720px)",
          background: "var(--mantine-color-dark-8, #1a1b1e)",
          overflow: "hidden",
          touchAction: "none",
          cursor: scale > 1 ? (dragging ? "grabbing" : "grab") : "zoom-in",
        }}
        onWheel={handleWheel}
        onDoubleClick={handleDoubleClick}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {src && (
          <img
            src={src}
            alt={alt ?? "image"}
            draggable={false}
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              maxWidth: "100%",
              maxHeight: "100%",
              transform: `translate(-50%, -50%) translate(${pos.x}px, ${pos.y}px) scale(${scale})`,
              transition: dragging ? "none" : "transform 0.08s ease-out",
              userSelect: "none",
            }}
          />
        )}
      </div>

      <Group justify="space-between" px="md" py="xs" wrap="nowrap">
        <Text size="xs" c="dimmed" visibleFrom="sm">
          Scroll or pinch to zoom · drag to pan · double-click to reset
        </Text>
        <Group gap={4} ml="auto">
          <ActionIcon
            variant="subtle"
            onClick={() => zoomBy(-ZOOM_STEP)}
            disabled={scale <= MIN_SCALE}
            aria-label="Zoom out"
          >
            <IconZoomOut size={18} />
          </ActionIcon>
          <ActionIcon variant="subtle" onClick={reset} disabled={isReset} aria-label="Reset zoom">
            <IconZoomReset size={18} />
          </ActionIcon>
          <ActionIcon
            variant="subtle"
            onClick={() => zoomBy(ZOOM_STEP)}
            disabled={scale >= MAX_SCALE}
            aria-label="Zoom in"
          >
            <IconZoomIn size={18} />
          </ActionIcon>
        </Group>
      </Group>
    </Modal>
  );
}
